import dayjs from "dayjs";
import { corDoLift } from "./promoHelpers";

const ehNumero = (valor) => typeof valor === "number" && Number.isFinite(valor);

const formatarDia = (iso) => (iso ? dayjs(iso).format("DD/MM/YYYY") : null);

const somar = (itens, pegar) =>
  itens.reduce((soma, item) => {
    const valor = pegar(item);
    return soma + (ehNumero(valor) ? valor : 0);
  }, 0);

const media = (valores) => {
  const validos = valores.filter(ehNumero);
  if (!validos.length) return null;
  return validos.reduce((soma, valor) => soma + valor, 0) / validos.length;
};

export const variacaoPct = (antes, depois) => {
  if (!ehNumero(antes) || !ehNumero(depois)) return null;
  if (antes > 0) return (depois / antes - 1) * 100;
  if (depois > 0) return Number.POSITIVE_INFINITY;
  return 0;
};

const eixoDoPainel = (antes, depois) => {
  const percent = variacaoPct(antes, depois);
  return {
    valores: { before_change: antes, after_change: depois },
    variacao: { percent, color: corDoLift(percent) },
  };
};

const porDia = (series, campo, combinar) => {
  const valores = new Map();
  series.forEach((serie) => {
    (serie?.dias || []).forEach((dia) => {
      if (!valores.has(dia.date)) valores.set(dia.date, []);
      valores.get(dia.date).push(dia[campo]);
    });
  });
  return new Map(
    [...valores.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([data, lista]) => [data, combinar(lista)])
  );
};

const somaDoDia = (lista) => somar(lista, (valor) => valor);

const mediaDoDia = (lista) => media(lista);

const grafico = (valoresPorDia, impacto) => ({
  x: [...valoresPorDia.keys()].map(formatarDia),
  y: [...valoresPorDia.values()].map((valor) =>
    ehNumero(valor) ? Number(valor.toFixed(2)) : 0
  ),
  x_annotation: formatarDia(impacto?.event_date),
  ...(impacto?.event_end_date
    ? { x_annotation_end: formatarDia(impacto.event_end_date) }
    : {}),
});

const mediaNosDias = (valoresPorDia, dias) =>
  media((dias || []).map((dia) => valoresPorDia.get(dia)));

export const seriesDasConsultas = (consultas) =>
  (consultas || []).flatMap((consulta) =>
    Array.isArray(consulta?.data?.events)
      ? consulta.data.events
      : [consulta?.data]
  );

export const analiseDaV2 = ({ impactos, series, tipo, zoneDescription }) => {
  const validos = (impactos || []).filter(Boolean);
  const seriesValidas = (series || []).filter(Boolean);
  if (!validos.length) return undefined;
  const [primeiro] = validos;
  const comAtencao = validos.filter((impacto) =>
    ehNumero(impacto.atencao?.media_diaria_antes)
  );
  const faturamento = eixoDoPainel(
    somar(validos, (impacto) => impacto.faturamento?.media_diaria_antes),
    somar(validos, (impacto) => impacto.faturamento?.media_diaria_depois)
  );
  const atencao = eixoDoPainel(
    comAtencao.length
      ? somar(comAtencao, (impacto) => impacto.atencao.media_diaria_antes)
      : null,
    comAtencao.length
      ? somar(comAtencao, (impacto) => impacto.atencao.media_diaria_depois)
      : null
  );
  const ocupacaoPorDia = porDia(seriesValidas, "ocupacao_pct", mediaDoDia);
  const ocupacao = eixoDoPainel(
    mediaNosDias(ocupacaoPorDia, primeiro.dias_antes),
    mediaNosDias(ocupacaoPorDia, primeiro.dias_depois)
  );
  return {
    kpi_data: {
      description: {
        event_type: tipo === "troca" ? "collection" : "event",
        end_datetime: primeiro.event_end_date,
      },
      revenue: faturamento.valores,
      revenue_variation: faturamento.variacao,
      permanence: atencao.valores,
      permanence_variation: atencao.variacao,
      occupation: ocupacao.valores,
      occupation_variation: ocupacao.variacao,
    },
    revenue_graph_data: grafico(
      porDia(seriesValidas, "faturamento_vendas", somaDoDia),
      primeiro
    ),
    graph_data: grafico(
      porDia(seriesValidas, "atencao_minutos", somaDoDia),
      primeiro
    ),
    occupation_graph_data: grafico(ocupacaoPorDia, primeiro),
    zone_description: zoneDescription ?? primeiro.zone_name ?? "Todas as zonas",
  };
};
